import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatAuditSummary } from "@/lib/auditSummary";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PersonAvatar from "@/components/PersonAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, ScrollText, Filter, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MANAGER_ROLES = new Set(["admin", "manager", "registered_manager",
  "deputy_manager", "service_manager", "super_admin"]);

const ACTION_TONE = {
  create: "bg-emerald-50 text-emerald-800 border-emerald-300",
  update: "bg-sky-50 text-sky-800 border-sky-300",
  delete: "bg-red-50 text-red-800 border-red-300",
  publish: "bg-indigo-50 text-indigo-800 border-indigo-300",
  confirm: "bg-violet-50 text-violet-800 border-violet-300",
};

export default function AuditCentre() {
  const { user } = useAuth();
  const canExport = MANAGER_ROLES.has((user?.role || "").toLowerCase());
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ module: "all", action: "all", date_from: "", date_to: "", actor: "" });

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filters.module !== "all") qs.set("module", filters.module);
    if (filters.action !== "all") qs.set("action", filters.action);
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    if (filters.actor.trim()) qs.set("actor", filters.actor.trim());
    qs.set("limit", "500");
    api.get(`/audit/events?${qs.toString()}`)
      .then((r) => { setItems(r.data.items || []); setModules(r.data.modules || {}); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters]); // load closes over filters

  const exportCsv = async () => {
    const qs = new URLSearchParams();
    if (filters.module !== "all") qs.set("module", filters.module);
    if (filters.action !== "all") qs.set("action", filters.action);
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    if (filters.actor.trim()) qs.set("actor", filters.actor.trim());
    try {
      const { downloadBlob } = await import("@/lib/api");
      await downloadBlob(`/audit/events.csv?${qs.toString()}`, `audit-log-${filters.date_from || "all"}-to-${filters.date_to || "now"}.csv`);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearFilters = () => setFilters({ module: "all", action: "all", date_from: "", date_to: "", actor: "" });
  const filterCount = (filters.module !== "all" ? 1 : 0) + (filters.action !== "all" ? 1 : 0) + (filters.date_from ? 1 : 0) + (filters.date_to ? 1 : 0) + (filters.actor.trim() ? 1 : 0);
  const moduleOptions = useMemo(() => Object.entries(modules), [modules]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="audit-title">Audit Centre</h1>
        </div>
        {canExport && (
          <Button size="sm" onClick={exportCsv} data-testid="btn-audit-csv" className="h-9 bg-slate-900 hover:bg-slate-800">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Export CSV
          </Button>
        )}
      </div>
      {/* Mobile header — Batch 7D */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
          <h1 className="font-heading text-base font-semibold truncate" data-testid="audit-title-mobile">Audit Centre</h1>
        </div>
        {canExport && (
          <Button size="sm" onClick={exportCsv} data-testid="btn-audit-csv-mobile" className="h-11 bg-slate-900 hover:bg-slate-800 shrink-0">
            <FileDown className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="p-4 lg:p-6 space-y-4">
        <div className="bg-white border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-end gap-3 lg:flex-wrap" data-testid="audit-filters">
          <div className="hidden lg:flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold self-end pb-2">
            <Filter className="w-3 h-3" /> Filters
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Module</Label>
            <Select value={filters.module} onValueChange={(v) => setFilters((f) => ({ ...f, module: v }))}>
              <SelectTrigger className="h-11 lg:h-8 w-full lg:w-56" data-testid="audit-filter-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {moduleOptions.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Action</Label>
            <Select value={filters.action} onValueChange={(v) => setFilters((f) => ({ ...f, action: v }))}>
              <SelectTrigger className="h-11 lg:h-8 w-full lg:w-36" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["all", "create", "update", "delete", "publish", "confirm"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="h-11 lg:h-8 w-full lg:w-40" data-testid="audit-filter-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="h-11 lg:h-8 w-full lg:w-40" data-testid="audit-filter-to" />
          </div>
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Actor email</Label>
            <Input value={filters.actor} onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))} placeholder="name@example.com" className="h-11 lg:h-8 w-full lg:w-52" data-testid="audit-filter-actor" />
          </div>
          {filterCount > 0 && (
            <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="btn-audit-clear" className="h-11 lg:h-8 text-xs col-span-1 sm:col-span-2 lg:col-span-1">
              <X className="w-3.5 h-3.5 mr-1" /> Clear {filterCount}
            </Button>
          )}
          <div className="lg:ml-auto text-[11px] text-slate-500 pb-2 col-span-1 sm:col-span-2 lg:col-span-1" data-testid="audit-count">{items.length} event{items.length !== 1 ? "s" : ""}</div>
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block bg-white border border-slate-200 overflow-x-auto" data-testid="audit-table-wrap">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading audit events…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="audit-empty">
              <ScrollText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              No audit events match the current filters.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs text-slate-700">
                  <th className="text-left px-3 py-2 font-semibold">When</th>
                  <th className="text-left px-3 py-2 font-semibold">Module</th>
                  <th className="text-left px-3 py-2 font-semibold">Action</th>
                  <th className="text-left px-3 py-2 font-semibold">Actor</th>
                  <th className="text-left px-3 py-2 font-semibold">Summary</th>
                  <th className="text-left px-3 py-2 font-semibold">Diff</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const summaryText = formatAuditSummary(e.summary);
                  return (
                  <tr key={e.id} className="border-b border-slate-100 align-top" data-testid={`audit-row-${e.id}`}>
                    <td className="px-3 py-2 tabular-nums text-xs whitespace-nowrap">{new Date(e.changed_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs">{modules[e.module] || e.module}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${ACTION_TONE[e.action] || "bg-slate-50 text-slate-700 border-slate-300"}`}>{e.action}</span></td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <PersonAvatar person={{ name: e.actor_name || e.actor_email }} size="xs" testid={`audit-avatar-${e.id}`} />
                        <div>
                          <div>{e.actor_name || e.actor_email}</div>
                          <div className="text-[10px] text-slate-500">{e.actor_role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700 max-w-xs truncate" title={summaryText}>{summaryText || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-600 max-w-md">
                      {(e.diff || []).slice(0, 4).map((d, i) => (
                        <div key={i} className="truncate"><span className="font-semibold">{d.field}:</span> <span className="text-slate-500">{JSON.stringify(d.from)}</span> → <span className="text-slate-900">{JSON.stringify(d.to)}</span></div>
                      ))}
                      {(e.diff || []).length > 4 && <div className="text-[10px] text-slate-400">+ {e.diff.length - 4} more…</div>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile audit card list — Batch 7D (read-only, no mutation) */}
        <div className="lg:hidden space-y-2" data-testid="audit-cards-mobile">
          {loading ? (
            <div className="p-4 text-sm text-slate-500 text-center">Loading audit events…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="audit-empty-mobile">
              <ScrollText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              No audit events match the current filters.
            </div>
          ) : (
            items.map((e) => {
              const summaryText = formatAuditSummary(e.summary);
              return (
              <div key={e.id} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`audit-card-${e.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${ACTION_TONE[e.action] || "bg-slate-50 text-slate-700 border-slate-300"}`}>{e.action}</span>
                  <div className="text-[11px] text-slate-500 tabular-nums text-right">{new Date(e.changed_at).toLocaleString()}</div>
                </div>
                <div className="mt-2 text-xs text-slate-700 font-semibold break-words">{modules[e.module] || e.module}</div>
                <div className="mt-1 flex items-center gap-2 text-[12px]">
                  <PersonAvatar person={{ name: e.actor_name || e.actor_email }} size="xs" testid={`audit-avatar-mobile-${e.id}`} />
                  <div className="min-w-0">
                    <div className="break-words">{e.actor_name || e.actor_email}</div>
                    <div className="text-[10px] text-slate-500">{e.actor_role}</div>
                  </div>
                </div>
                {summaryText && <div className="mt-2 text-xs text-slate-700 break-words">{summaryText}</div>}
                {(e.diff || []).length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-600 space-y-0.5">
                    {(e.diff || []).slice(0, 4).map((d, i) => (
                      <div key={i} className="break-words"><span className="font-semibold">{d.field}:</span> <span className="text-slate-500">{JSON.stringify(d.from)}</span> → <span className="text-slate-900">{JSON.stringify(d.to)}</span></div>
                    ))}
                    {(e.diff || []).length > 4 && <div className="text-[10px] text-slate-400">+ {e.diff.length - 4} more…</div>}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
